import { IEmpresa, IMemoriaCalculo } from "../../model";
import { LookupStore } from "../../stores/LookupStore";

/**
 * Filtro de texto exibido na linha de filtros (entre "Estado" e os demais).
 *
 * @property kind        Discriminante do tipo de filtro; sempre "text".
 * @property filterKey   Chave correspondente em `MemoriaStore.obs.filters`.
 * @property label       Rótulo do campo exibido ao usuário.
 * @property placeholder Texto de placeholder; default `Filtrar (${label})`.
 * @property colProps    Props repassadas ao componente `<MDBCol>`.
 */
export interface MemoriaFilterText {
  kind: "text";
  filterKey: string;
  label: string;
  placeholder?: string;
  colProps: any;
}

/**
 * Filtro de CNPJ origem/destino, comum a todos os modelos.
 *
 * O campo renderizado alterna entre "CNPJ Origem" e "CNPJ Destino"
 * conforme o tipo de operação (entrada/saída) selecionado.
 *
 * @property kind Discriminante do tipo de filtro; sempre "cnpj".
 */
export interface MemoriaFilterCnpj {
  kind: "cnpj";
}

/**
 * União discriminada dos filtros suportados na linha de filtros.
 * O campo `kind` define qual variante deve ser renderizada.
 */
export type MemoriaFilter = MemoriaFilterText | MemoriaFilterCnpj;

/**
 * Coluna exibida na tabela de memórias de cálculo.
 *
 * @property dataField Campo do item (vide {@link MemoriaConfig.mapItem}) a ser exibido.
 * @property label     Rótulo do cabeçalho da coluna.
 * @property width     Largura da coluna (ex.: "50px", "150px").
 */
export interface MemoriaColumn {
  dataField: string;
  label: string;
  width: string;
}

/**
 * Campo de formulário do modal de criação/edição de memória.
 *
 * @property key      Chave correspondente em `MemoriaStore.obs.create_memoria`.
 * @property label    Rótulo do campo exibido ao usuário.
 * @property required Indica se o campo é obrigatório (exibe "*" no rótulo).
 * @property width    Largura do campo (ex.: "25%", "50%", "100%").
 */
export interface MemoriaFormField {
  key: string;
  label: string;
  required?: boolean;
  width: string;
}

/**
 * Regra de validação aplicada no salvamento da memória.
 *
 * @property key     Chave em `MemoriaStore.obs.create_memoria` que deve estar preenchida.
 * @property message Mensagem de aviso exibida (toast) quando o campo está vazio.
 */
export interface MemoriaRequired {
  key: string;
  message: string;
}

/**
 * Configuração que descreve o comportamento de {@link MemoriaBase} para um
 * modelo específico de documento fiscal (NF-e, NFC-e, CT-e, NFS-e).
 *
 * Centraliza filtros, colunas, campos do formulário, validações e mapeamento
 * de dados, permitindo que um único componente atenda a todos os modelos.
 *
 * @property modelo         Identificador do modelo fiscal (ex.: "nfe", "cte").
 * @property filters        Filtros exibidos na linha de filtros.
 * @property columnsBefore  Colunas inseridas entre "Nome do Cálculo" e "CNPJ Destino".
 * @property columns        Colunas inseridas entre "CNPJ Origem" e "Data do Cadastro".
 * @property formRows       Linhas de campos do modal de criação/edição.
 * @property required       Validações aplicadas ao salvar.
 * @property mapItem        Mapeia uma memória para os campos específicos do modelo na tabela.
 * @property onEmpresaSelect Hook opcional disparado ao selecionar uma empresa.
 */
export interface MemoriaConfig {
  modelo: string;
  filters: MemoriaFilter[];
  columnsBefore?: MemoriaColumn[];
  columns: MemoriaColumn[];
  formRows: MemoriaFormField[][];
  required: MemoriaRequired[];
  mapItem: (memoria: IMemoriaCalculo) => any;
  onEmpresaSelect?: (empresa: IEmpresa) => void;
}

/** Filtros compartilhados por NF-e e NFC-e (CEST, NCM, CFOP, CNPJ e Descrição). */
const NOTA_FILTERS: MemoriaFilter[] = [
  { kind: "text", filterKey: "cest", label: "CEST", colProps: { size: "6", md: "3", xl: "2" } },
  { kind: "text", filterKey: "ncm", label: "NCM", colProps: { size: "6", md: "3", xl: "2" } },
  { kind: "text", filterKey: "cfop", label: "CFOP", colProps: { size: "6", md: "3", xl: "2" } },
  { kind: "cnpj" },
  { kind: "text", filterKey: "descricao", label: "Descrição", colProps: { size: "12", md: "6", xl: "4" } },
];

/** Coluna combinada "CEST / NCM" exibida antes do CNPJ para NF-e/NFC-e. */
const NOTA_COLUMNS_BEFORE: MemoriaColumn[] = [
  { dataField: "cest_ncm", label: "CEST / NCM", width: "50px" },
];

/** Colunas de NF-e/NFC-e (Descrição, CFOP, CST, PIS e COFINS). */
const NOTA_COLUMNS: MemoriaColumn[] = [
  { dataField: "descricao", label: "Descrição", width: "150px" },
  { dataField: "cfop", label: "CFOP", width: "50px" },
  { dataField: "cst", label: "CST", width: "50px" },
  { dataField: "pis", label: "PIS", width: "50px" },
  { dataField: "cofins", label: "COFINS", width: "50px" },
];

/** Linhas de campos do modal de criação/edição para NF-e/NFC-e. */
const NOTA_FORM_ROWS: MemoriaFormField[][] = [
  [
    { key: "cest", label: "CEST", required: false, width: "25%" },
    { key: "cfop", label: "CFOP", required: true, width: "25%" },
  ],
  [
    { key: "cofins", label: "COFINS", required: true, width: "25%" },
    { key: "cst", label: "CST", required: true, width: "25%" },
    { key: "ncm", label: "NCM", required: true, width: "25%" },
    { key: "pis", label: "PIS", required: true, width: "25%" },
  ],
  [{ key: "descricao", label: "Descrição", required: true, width: "100%" }],
];

/** Validações de campos obrigatórios no salvamento para NF-e/NFC-e. */
const NOTA_REQUIRED: MemoriaRequired[] = [
  { key: "cfop", message: "Por favor, digite um CFOP." },
  { key: "cofins", message: "Por favor, digite um COFINS." },
  { key: "cst", message: "Por favor, digite um CST." },
  { key: "ncm", message: "Por favor, digite um NCM." },
  { key: "pis", message: "Por favor, digite um PIS." },
  { key: "descricao", message: "Por favor, digite uma Descrição." },
];

/** Mapeia uma memória para os campos exibidos na tabela de NF-e/NFC-e. */
const NOTA_MAP_ITEM = (memoria: IMemoriaCalculo) => ({
  cest_ncm: (memoria.cest || "Sem Cest") + " / " + memoria.ncm,
  cest: memoria.cest,
  ncm: memoria.ncm,
  descricao: memoria.descricao,
  cfop: memoria.cfop,
  cst: memoria.cst,
  pis: memoria.pis,
  cofins: memoria.cofins,
});

/** Configuração da memória de cálculo para NF-e. */
export const NFE_CONFIG: MemoriaConfig = {
  modelo: "nfe",
  filters: NOTA_FILTERS,
  columnsBefore: NOTA_COLUMNS_BEFORE,
  columns: NOTA_COLUMNS,
  formRows: NOTA_FORM_ROWS,
  required: NOTA_REQUIRED,
  mapItem: NOTA_MAP_ITEM,
};

/** Configuração da memória de cálculo para NFC-e (idêntica à NF-e, exceto o modelo). */
export const NFCE_CONFIG: MemoriaConfig = {
  ...NFE_CONFIG,
  modelo: "nfce",
};

/** Configuração da memória de cálculo para CT-e (apenas CFOP e CST). */
export const CTE_CONFIG: MemoriaConfig = {
  modelo: "cte",
  filters: [
    { kind: "text", filterKey: "cfop", label: "CFOP", colProps: { size: "6", md: "3", xl: "2" } },
    { kind: "cnpj" },
  ],
  columns: [
    { dataField: "cfop", label: "CFOP", width: "50px" },
    { dataField: "cst", label: "CST", width: "50px" },
  ],
  formRows: [
    [
      { key: "cfop", label: "CFOP", required: true, width: "25%" },
      { key: "cst", label: "CST", required: true, width: "25%" },
    ],
  ],
  required: [
    { key: "cfop", message: "Por favor, digite um CFOP." },
    { key: "cst", message: "Por favor, digite um CST." },
  ],
  mapItem: (memoria) => ({
    cfop: memoria.cfop,
    cst: memoria.cst,
  }),
};

/**
 * Configuração da memória de cálculo para NFS-e.
 *
 * Usa o "Cod. Trib. Nacional" como campo característico e carrega as
 * classificações de ICMS da UF da empresa ao selecioná-la.
 */
export const NFSE_CONFIG: MemoriaConfig = {
  modelo: "nfse",
  filters: [
    {
      kind: "text",
      filterKey: "cod_trib_nacional",
      label: "Cod. Trib. Nacional",
      colProps: { size: "12", sm: "6", md: "4", xl: "2" },
    },
    { kind: "cnpj" },
  ],
  columns: [
    { dataField: "cod_trib_nacional", label: "Cod. Trib. Nacional", width: "80px" },
  ],
  formRows: [
    [{ key: "cod_trib_nacional", label: "Cod. Trib. Nacional", required: true, width: "50%" }],
  ],
  required: [
    { key: "cod_trib_nacional", message: "Por favor, digite um Cod. Trib. Nacional." },
  ],
  mapItem: (memoria) => ({
    cod_trib_nacional: memoria.cod_trib_nacional,
  }),
  onEmpresaSelect: (empresa) => {
    LookupStore.loadIcmsClassificacoes(empresa.uf);
  },
};
